// Turn aligned data in a question (a frequency row, a set of readings, a
// results grid) into table rows. Prose, poems, and working stay as lines.

function isNumToken(token) {
  return /^[+-]?\d+(?:\.\d+)?(?:\s*[-–—]\s*[+-]?\d+(?:\.\d+)?)?(?:[:/]\d+(?:\.\d+)?)?(?:°|º)?(?:%|[A-Za-z]{1,4})?$/.test(token);
}

function isShort(cell) {
  const text = String(cell || '').trim();
  if (!text || text.length > 32) return false;
  return text.split(/\s+/).length <= 5;
}

function pipeCells(line) {
  const text = String(line || '').trim();
  if ((text.match(/\|/g) || []).length < 2) return null;
  const cells = text.split('|').map((part) => part.trim()).filter((part) => part !== '');
  if (cells.length < 2) return null;
  if (cells.every((cell) => /^:?-{2,}:?$/.test(cell))) return { separator: true, cells };
  if (!cells.every(isShort)) return null;
  return { cells };
}

function wideCells(line) {
  const text = String(line || '').trim();
  if (!/\s{2,}|\t/.test(text)) return null;
  const cells = text.split(/\s{2,}|\t+/).map((part) => part.trim()).filter(Boolean);
  if (cells.length < 2 || !cells.every(isShort)) return null;
  return cells;
}

function numericCells(line) {
  const tokens = String(line || '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 4) return null;
  const cols = [];
  let i = tokens.length;
  while (i > 0) {
    if (i >= 3 && isNumToken(tokens[i - 3]) && /^[-–—]$/.test(tokens[i - 2]) && isNumToken(tokens[i - 1])) {
      cols.unshift(`${tokens[i - 3]} - ${tokens[i - 1]}`);
      i -= 3;
      continue;
    }
    if (isNumToken(tokens[i - 1])) {
      cols.unshift(tokens[i - 1]);
      i -= 1;
      continue;
    }
    break;
  }
  if (cols.length < 3) return null;
  const label = tokens.slice(0, i).join(' ').trim();
  if (label.split(/\s+/).filter(Boolean).length > 5) return null;
  if (label.length > 40) return null;
  return [label, ...cols];
}

function countsMatch(rows) {
  const counts = rows.map((row) => row.length);
  const max = Math.max(...counts);
  const min = Math.min(...counts);
  if (max < 2 || max - min > 1) return null;
  return rows.map((row) => {
    const copy = row.slice();
    while (copy.length < max) copy.push('');
    return copy;
  });
}

function hasDigits(rows) {
  return rows.some((row) => row.some((cell) => /\d/.test(cell)));
}

function takeRun(lines, start, read) {
  const rows = [];
  let i = start;
  while (i < lines.length) {
    const text = String(lines[i] || '').trim();
    if (!text) break;
    const readRow = read(text);
    if (!readRow) break;
    if (readRow.separator) {
      i += 1;
      continue;
    }
    const cells = readRow.cells || readRow;
    if (!cells || cells.length < 2) break;
    rows.push(cells);
    i += 1;
  }
  if (rows.length < 2) return null;
  const padded = countsMatch(rows);
  if (!padded || !hasDigits(padded)) return null;
  return { rows: padded, consumed: i - start };
}

function tableAt(lines, start) {
  const line = String(lines[start] || '');
  if (pipeCells(line)) {
    const run = takeRun(lines, start, (text) => pipeCells(text));
    if (run) return run;
  }
  if (wideCells(line)) {
    const run = takeRun(lines, start, (text) => {
      const cells = wideCells(text);
      return cells ? { cells } : null;
    });
    if (run) return run;
  }
  if (numericCells(line)) {
    const run = takeRun(lines, start, (text) => {
      const cells = numericCells(text);
      return cells ? { cells } : null;
    });
    if (run) return run;
  }
  return null;
}

export function isDataLine(line) {
  return !!(pipeCells(line) || (wideCells(line) && wideCells(line).length >= 3) || numericCells(line));
}

function firstRowIsHeader(rows) {
  if (rows.length < 2) return false;
  const headHasDigit = rows[0].slice(1).some((cell) => /\d/.test(cell));
  const bodyHasDigit = rows.slice(1).some((row) => row.slice(1).some((cell) => /\d/.test(cell)));
  return !headHasDigit && bodyHasDigit;
}

export function segmentLines(lines) {
  const source = Array.isArray(lines) ? lines : [];
  const out = [];
  let i = 0;
  while (i < source.length) {
    const text = String(source[i] || '').trim();
    if (!text) {
      i += 1;
      continue;
    }
    const table = tableAt(source, i);
    if (table) {
      out.push({
        type: 'table',
        rows: table.rows,
        header: firstRowIsHeader(table.rows)
      });
      i += table.consumed;
      continue;
    }
    const indexes = [];
    while (i < source.length && !tableAt(source, i)) {
      indexes.push(i);
      i += 1;
    }
    while (indexes.length && !String(source[indexes[indexes.length - 1]] || '').trim()) indexes.pop();
    if (indexes.length) out.push({ type: 'text', indexes });
  }
  return out;
}
